import React, { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
    CellValueChangedEvent,
    ColDef,
    themeQuartz,
} from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import { TrashIcon, PlusIcon } from "lucide-react";

import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import {
    useGetOwnerGroupsWithCountsQuery,
    useAddOwnerGroupMutation,
    useUpdateOwnerGroupMutation,
    useDeleteOwnerGroupMutation,
} from "@/store/booking-management-api";
import { OwnerGroupElement } from "@/models/responses";
import {
    DeleteOptionConfirmDialog,
} from "@/lib/shadcn/components/delete-option-confirm-dialog";
import {
    BlockingApartment,
    DeleteOptionBlockedDialog,
} from "@/lib/shadcn/components/delete-option-blocked-dialog";
import {
    extractErrorMessage,
    isOwnerGroupInUseError,
} from "@/utils/inUseError";

interface OwnerGroupRow {
    id: number;
    name: string;
    monthlyFee: number;
    activeApartmentCount: number;
}

interface ActionsRendererProps extends CustomCellRendererProps<OwnerGroupRow> {
    onDelete: (row: OwnerGroupRow) => void;
}

const ActionsRenderer: React.FC<ActionsRendererProps> = ({ data, onDelete }) => {
    if (!data) return null;
    return (
        <div className="text-right">
            <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete owner group ${data.name}`}
                data-testid={`delete-owner-group-button-${data.id}`}
                onClick={() => onDelete(data)}
                className="text-destructive hover:text-destructive h-8 w-8 p-0"
            >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
        </div>
    );
};

const OwnerGroupsManagementPage: React.FC = () => {
    const { data: ownerGroups } = useGetOwnerGroupsWithCountsQuery();
    const [addOwnerGroup] = useAddOwnerGroupMutation();
    const [updateOwnerGroup] = useUpdateOwnerGroupMutation();
    const [deleteOwnerGroup] = useDeleteOwnerGroupMutation();

    const [showAddInputs, setShowAddInputs] = useState(false);
    const [addNameValue, setAddNameValue] = useState("");
    const [addFeeValue, setAddFeeValue] = useState("");
    const [addError, setAddError] = useState<string | undefined>(undefined);

    const [renameError, setRenameError] = useState<string | undefined>(undefined);
    /**
     * `setDataValue` (called when reverting a failed rename or an invalid
     * fee edit) re-fires `onCellValueChanged` with the reverted value. Track
     * which (rowId, colId) pairs are mid-revert so the second event is
     * dropped instead of issuing a spurious PUT to undo the revert itself.
     */
    const revertingRowsRef = useRef<Set<string>>(new Set());

    const [pendingDelete, setPendingDelete] = useState<OwnerGroupRow | undefined>(undefined);
    const [isDeleting, setIsDeleting] = useState(false);
    const [blockedDialogState, setBlockedDialogState] = useState<
        | { ownerGroupName: string; blockingApartments: BlockingApartment[] }
        | undefined
    >(undefined);
    const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

    const rows: OwnerGroupRow[] = useMemo(
        () =>
            (ownerGroups ?? []).map((g: OwnerGroupElement) => ({
                id: g.id,
                name: g.name,
                monthlyFee: g.monthlyFee,
                activeApartmentCount: g.activeApartmentCount,
            })),
        [ownerGroups],
    );

    const handleDeleteClick = useCallback((row: OwnerGroupRow) => {
        setPendingDelete(row);
        setDeleteError(undefined);
    }, []);

    const columnDefs: ColDef<OwnerGroupRow>[] = useMemo(
        () => [
            {
                field: "name",
                headerName: "Name",
                cellDataType: "text",
                editable: true,
                cellEditor: "agTextCellEditor",
                flex: 2,
                sortable: true,
                filter: "agTextColumnFilter",
            },
            {
                field: "monthlyFee",
                headerName: "Monthly Fee",
                cellDataType: "number",
                editable: true,
                cellEditor: "agNumberCellEditor",
                cellEditorParams: { min: 0, precision: 2 },
                width: 160,
                sortable: true,
                filter: "agNumberColumnFilter",
            },
            {
                field: "activeApartmentCount",
                headerName: "Active Apartments",
                cellDataType: "number",
                sortable: true,
                editable: false,
                sort: "asc",
                width: 220,
                filter: "agNumberColumnFilter",
            },
            {
                field: "actions" as keyof OwnerGroupRow,
                headerName: "Actions",
                cellRenderer: ActionsRenderer,
                cellRendererParams: {
                    onDelete: handleDeleteClick,
                },
                editable: false,
                sortable: false,
                filter: false,
                width: 140,
            },
        ],
        [handleDeleteClick],
    );

    const defaultColDef = useMemo<ColDef<OwnerGroupRow>>(
        () => ({
            resizable: true,
            sortable: true,
            cellStyle: {
                textAlign: "left",
                paddingLeft: "18px",
                fontSize: "13px",
            },
        }),
        [],
    );

    const onAddSubmit = useCallback(async () => {
        const trimmedName = addNameValue.trim();
        if (!trimmedName) {
            setAddError("Name is required");
            return;
        }
        // Empty fee defaults to 0.
        const rawFee = addFeeValue.trim();
        const parsedFee = rawFee === "" ? 0 : Number(rawFee);
        if (!Number.isFinite(parsedFee) || parsedFee < 0) {
            setAddError("Monthly fee must be a non-negative number");
            return;
        }
        setAddError(undefined);
        try {
            await addOwnerGroup({ name: trimmedName, monthlyFee: parsedFee }).unwrap();
            setAddNameValue("");
            setAddFeeValue("");
            setShowAddInputs(false);
        } catch (err) {
            setAddError(extractErrorMessage(err) ?? "Could not add owner group");
        }
    }, [addNameValue, addFeeValue, addOwnerGroup]);

    const handleNameChange = useCallback(
        async (event: CellValueChangedEvent<OwnerGroupRow>) => {
            const rowId = event.data.id;
            const revertKey = `${rowId}:name`;
            const newName = String(event.newValue ?? "").trim();
            const oldName = String(event.oldValue ?? "");
            if (newName === oldName) {
                return;
            }
            if (!newName) {
                revertingRowsRef.current.add(revertKey);
                event.node.setDataValue("name", oldName);
                setRenameError("Name is required");
                return;
            }
            setRenameError(undefined);
            try {
                await updateOwnerGroup({
                    id: rowId,
                    name: newName,
                    monthlyFee: event.data.monthlyFee,
                }).unwrap();
            } catch (err) {
                revertingRowsRef.current.add(revertKey);
                event.node.setDataValue("name", oldName);
                setRenameError(
                    extractErrorMessage(err) ?? "Could not rename owner group",
                );
            }
        },
        [updateOwnerGroup],
    );

    const handleFeeChange = useCallback(
        async (event: CellValueChangedEvent<OwnerGroupRow>) => {
            const rowId = event.data.id;
            const revertKey = `${rowId}:monthlyFee`;
            const oldFee = Number(event.oldValue ?? 0);
            const newFeeRaw = event.newValue;
            const newFee =
                typeof newFeeRaw === "number"
                    ? newFeeRaw
                    : Number(String(newFeeRaw ?? "").trim());

            if (newFee === oldFee) {
                return;
            }
            if (!Number.isFinite(newFee) || newFee < 0) {
                revertingRowsRef.current.add(revertKey);
                event.node.setDataValue("monthlyFee", oldFee);
                setRenameError("Monthly fee must be a non-negative number");
                return;
            }
            setRenameError(undefined);
            try {
                await updateOwnerGroup({
                    id: rowId,
                    name: event.data.name,
                    monthlyFee: newFee,
                }).unwrap();
            } catch (err) {
                revertingRowsRef.current.add(revertKey);
                event.node.setDataValue("monthlyFee", oldFee);
                setRenameError(
                    extractErrorMessage(err) ?? "Could not update owner group",
                );
            }
        },
        [updateOwnerGroup],
    );

    const onCellValueChanged = useCallback(
        async (event: CellValueChangedEvent<OwnerGroupRow>) => {
            const colId = event.colDef.field;
            if (colId !== "name" && colId !== "monthlyFee") return;
            const rowId = event.data.id;
            const revertKey = `${rowId}:${colId}`;
            // Skip the synthetic event fired by `setDataValue` during a revert.
            if (revertingRowsRef.current.has(revertKey)) {
                revertingRowsRef.current.delete(revertKey);
                return;
            }
            if (colId === "name") {
                await handleNameChange(event);
            } else {
                await handleFeeChange(event);
            }
        },
        [handleNameChange, handleFeeChange],
    );

    const onConfirmDelete = useCallback(async () => {
        if (!pendingDelete) return;
        setIsDeleting(true);
        setDeleteError(undefined);
        const target = pendingDelete;
        try {
            await deleteOwnerGroup({ id: target.id }).unwrap();
            setPendingDelete(undefined);
        } catch (err) {
            if (isOwnerGroupInUseError(err)) {
                const entry = err.errors[0];
                setBlockedDialogState({
                    ownerGroupName: target.name,
                    blockingApartments: entry.blockingApartments.map((b) => ({
                        apartmentId: b.apartmentId,
                        apartmentName: b.apartmentName,
                    })),
                });
                setPendingDelete(undefined);
            } else {
                setDeleteError("Could not delete owner group");
                setPendingDelete(undefined);
            }
        } finally {
            setIsDeleting(false);
        }
    }, [deleteOwnerGroup, pendingDelete]);

    return (
        <div
            data-testid="owner-groups-management-page"
            className="flex flex-col gap-2 p-4"
        >
            <div className="flex items-center justify-between">
                <h1 className="text-lg font-semibold text-gray-900">Owner Groups</h1>
                <div className="flex items-center gap-2">
                    {showAddInputs ? (
                        <>
                            <Input
                                data-testid="owner-groups-management-add-name-input"
                                aria-label="Owner group name"
                                value={addNameValue}
                                onChange={(e) => {
                                    setAddNameValue(e.target.value);
                                    setAddError(undefined);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        void onAddSubmit();
                                    }
                                }}
                                placeholder="Owner group name"
                                className="w-56"
                            />
                            <Input
                                data-testid="owner-groups-management-add-fee-input"
                                aria-label="Monthly fee"
                                inputMode="decimal"
                                value={addFeeValue}
                                onChange={(e) => {
                                    setAddFeeValue(e.target.value);
                                    setAddError(undefined);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        void onAddSubmit();
                                    }
                                }}
                                placeholder="Monthly fee"
                                className="w-32"
                            />
                            <Button
                                size="sm"
                                data-testid="owner-groups-management-add-submit"
                                onClick={() => void onAddSubmit()}
                            >
                                Add
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setShowAddInputs(false);
                                    setAddNameValue("");
                                    setAddFeeValue("");
                                    setAddError(undefined);
                                }}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <Button
                            size="sm"
                            data-testid="owner-groups-management-add-button"
                            onClick={() => setShowAddInputs(true)}
                        >
                            <PlusIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
                            Add owner group
                        </Button>
                    )}
                </div>
            </div>

            {addError && (
                <div
                    data-testid="owner-groups-management-add-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {addError}
                </div>
            )}
            {renameError && (
                <div
                    data-testid="owner-groups-management-rename-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {renameError}
                </div>
            )}
            {deleteError && (
                <div
                    data-testid="owner-groups-management-delete-error"
                    role="alert"
                    className="text-sm text-destructive"
                >
                    {deleteError}
                </div>
            )}

            <div className="ag-theme-quartz h-[700px] rounded-md border border-gray-200">
                <AgGridReact<OwnerGroupRow>
                    theme={themeQuartz}
                    rowData={rows}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onCellValueChanged={onCellValueChanged}
                    getRowId={(params) => params.data.id.toString()}
                    animateRows={true}
                    stopEditingWhenCellsLoseFocus={true}
                    singleClickEdit={false}
                />
            </div>

            <DeleteOptionConfirmDialog
                optionTypeLabel="owner group"
                optionName={pendingDelete?.name ?? ""}
                open={pendingDelete !== undefined}
                onOpenChange={(open) => {
                    if (!open) setPendingDelete(undefined);
                }}
                onConfirm={() => void onConfirmDelete()}
                isDeleting={isDeleting}
            />
            <DeleteOptionBlockedDialog
                optionTypeLabel="owner group"
                optionName={blockedDialogState?.ownerGroupName ?? ""}
                blockingApartments={blockedDialogState?.blockingApartments ?? []}
                open={blockedDialogState !== undefined}
                onOpenChange={(open) => {
                    if (!open) setBlockedDialogState(undefined);
                }}
            />
        </div>
    );
};

export default OwnerGroupsManagementPage;
